import React, { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { appConfig } from '../../../settings';
import { IDataTypeMain, IMonthlyOrderOrder, monthlyOrderStatuses } from '../../../interfaces';
import { useMonthlyOrder } from '../../../hooks/stores/useMonthlyOrderStores';
import { postMonthlyOrderLinkUnlinkOrder } from '../../../middleware';
import { cleanColumnsDataSource } from '../../../instruments';
import { tF, tI } from '../../../translate';
import { Catcher, LinkComponent, TableItemToolbar, columnToolbar } from '../../../Components';
import Styles from '../../styles/m_viewStyles.less';

type IDataType = IDataTypeMain &
    Omit<IMonthlyOrderOrder, 'purchase_order_number'> & {
        idLink?: ReactNode;
        descriptionLink?: ReactNode;
        status?: ReactNode;
        createdAt?: ReactNode;
        updatedAt?: ReactNode;
        toolbar?: ReactNode;
    };

type IMonthlyOrderOrdersTable = {
    isLoading?: boolean;
};
export const MonthlyOrderOrdersTable: React.FC<IMonthlyOrderOrdersTable> = observer(
    ({ isLoading }: IMonthlyOrderOrdersTable) => {
        const { id, linkedOrders: list, status: monthlyOrderStatus } = useMonthlyOrder();

        const unLinkOrder = async (orderId: number): Promise<void> => {
            if (id && orderId) {
                await postMonthlyOrderLinkUnlinkOrder({
                    monthlyOrder: id!.toString(),
                    orderId: orderId!.toString(),
                    linkening: false,
                });
            }
        };

        const tableColumns: ColumnsType<IDataType> = [
            {
                title: tF('ID'),
                dataIndex: 'idLink',
            },
            {
                title: tF('Amount'),
                dataIndex: 'amount',
                className: Styles.columnRight,
            },
            {
                title: tF('purchase order number'),
                dataIndex: 'purchaseOrderNumber',
                className: Styles.columnRight,
            },
            {
                title: tF('Description'),
                dataIndex: 'descriptionLink',
            },
            {
                title: tF('Created at'),
                dataIndex: 'createdAt',
            },
            {
                title: tF('Updated at'),
                dataIndex: 'updatedAt',
            },
            columnToolbar,
        ];
        const tableData: IDataType[] =
            list?.map((order: IMonthlyOrderOrder) => {
                const deleteItem = (): void => {
                    unLinkOrder(order.id);
                };

                return {
                    key: order.id,
                    ...order,
                    idLink:
                        monthlyOrderStatus === monthlyOrderStatuses.DRAFT ? (
                            <LinkComponent currentListComponent={'BillingOrdersList'} id={order.id.toString()} />
                        ) : (
                            id
                        ),
                    descriptionLink: order.description ? (
                        <LinkComponent
                            currentListComponent={'OrdersList'}
                            id={order.id.toString()}
                            isLight
                            name={order.description}
                        />
                    ) : null,
                    createdAt: order.createdAt && dayjs(order.createdAt).format(appConfig.formatDateTime),
                    updatedAt: order.updatedAt && dayjs(order.updatedAt).format(appConfig.formatDateTime),
                    toolbar: (
                        <TableItemToolbar
                            currentListComponent={'MonthlyOrdersList'}
                            deleteItem={deleteItem}
                            id={order?.id.toString() || ''}
                            isBigButtons
                            isDelete={monthlyOrderStatus === monthlyOrderStatuses.DRAFT}
                            itemName={`"${order.id}" ${tI('order')}`}
                        />
                    ),
                };
            }) || [];

        return (
            <Catcher>
                <div className={Styles.tablePlace}>
                    <Table
                        className={'table-theme'}
                        {...cleanColumnsDataSource<IDataType>(tableColumns, tableData)}
                        loading={isLoading}
                        pagination={false}
                        size={'middle'}
                    />
                </div>
            </Catcher>
        );
    },
);
